import LessonMarkdown from './LessonMarkdown';
import { fallbackNote, markdownBlocks, resolveField, type ContentLang } from '../lib/contentText';

interface LessonCopyProps {
  /** Journey setting. English-first paths ignore the reader toggle. */
  primary: 'my' | 'en';
  /** Reader toggle, used only when primary is Burmese. */
  lang: ContentLang;
  my?: string | null;
  en?: string | null;
  legacy?: unknown;
}

/**
 * Burmese-first paths show one language, falling back when a field is empty.
 * English-first paths (the CA DMV course) show each English block with the
 * matching Burmese block in smaller type underneath.
 */
export default function LessonCopy({ primary, lang, my, en, legacy }: LessonCopyProps) {
  if (primary === 'en') {
    const english = markdownBlocks((en || '').trim() ? en : legacy);
    const burmese = markdownBlocks(my);
    const count = Math.max(english.length, burmese.length);
    if (count === 0) return null;
    return (
      <div className="space-y-4">
        {Array.from({ length: count }, (_, index) => (
          <div key={index}>
            {english[index] && <LessonMarkdown value={english[index]} />}
            {burmese[index] && (
              <div className="lesson-subtext font-myanmar" lang="my">
                <LessonMarkdown value={burmese[index]} />
              </div>
            )}
          </div>
        ))}
      </div>
    );
  }

  const resolved = resolveField(lang, my, en, legacy);
  if (!resolved.text) return null;
  return (
    <>
      {resolved.fallback && (
        <p className="mb-3 text-sm text-amber-800">{fallbackNote(lang === 'my' ? 'en' : 'my')}</p>
      )}
      <LessonMarkdown value={resolved.text} />
    </>
  );
}

export function KeyTerms({
  glossary,
}: {
  glossary?: { termEn: string; termMy: string }[];
}) {
  const terms = (glossary || []).filter((term) => term.termEn.trim() || term.termMy.trim());
  if (terms.length === 0) return null;
  return (
    <section className="rounded-2xl border border-gray-200 bg-white p-4 sm:p-6">
      <h2 className="text-lg font-bold text-gray-900">Key terms</h2>
      <p className="mt-1 text-sm text-gray-500">English term, then the Burmese translation.</p>
      <dl className="mt-4 space-y-3">
        {terms.map((term) => (
          <div key={`${term.termEn}-${term.termMy}`} className="grid gap-1 sm:grid-cols-[minmax(8rem,14rem)_1fr] sm:gap-4">
            <dt className="font-semibold text-gray-900">{term.termEn}</dt>
            <dd className="lesson-subtext font-myanmar" lang="my">{term.termMy}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}
